import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useAuth} from '../auth'
import {RemovedProduct} from './core/_models'
import {getRemovedProductsNeedingConfirmation} from './core/_requests'
import {LoadingState, ErrorState, NoDataState} from '../../components/common/TableStates'
import {ImageModal} from '../../components/common/ImageModal'
import {Button, Form} from 'react-bootstrap'
import Paginator from '../../modules/common/Paginator'
import {useSearchParams} from 'react-router-dom'
import RemovedProductsTable from './components/RemovedProductsTable'
import {TutorialVideoLink} from '../../tenant/TutorialVideoLink'

const RemovedProductsList: React.FC = () => {
  const {formatMessage, locale: language} = useIntl()
  const {auth} = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [products, setProducts] = useState<RemovedProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [poIdFilterInput, setPoIdFilterInput] = useState<string>('')
  const [appliedPoIdFilter, setAppliedPoIdFilter] = useState<string | null>(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null)
  const [showVideoHighlight, setShowVideoHighlight] = useState(false)

  useEffect(() => {
    const hasSeenVideo = document.cookie
      .split(';')
      .some(item => item.trim().startsWith('removed_products_video='));

    setShowVideoHighlight(!hasSeenVideo);
  }, []);

  // Pagination settings
  const perPage = 20
  const currentPage = parseInt(searchParams.get('current_page') || '1', 10)

  const fetchProducts = async () => {
    if (!loading) setLoading(true)
    try {
      // Convert PO ID filter to number if provided, remove # prefix
      const poIdValue = appliedPoIdFilter && appliedPoIdFilter.trim() !== '' 
        ? (appliedPoIdFilter.trim().startsWith('#') 
            ? appliedPoIdFilter.trim().slice(1) 
            : appliedPoIdFilter.trim())
        : null

      const response = await getRemovedProductsNeedingConfirmation(
        auth?.token,
        language,
        true, // Include confirmed products
        poIdValue ? parseInt(poIdValue, 10) : null
      )
      // Handle different response structures
      let productsData: RemovedProduct[] = []
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        productsData = response.data.data
      } 
      else if (Array.isArray(response.data)) {
        productsData = response.data
      }
      else if (response.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
        productsData = Object.values(response.data.data) as RemovedProduct[]
      }
      
      // Filter out invalid entries
      const processedProducts = productsData.filter((p) => {
        return p != null && p.removed_po_item_id != null
      })
      
      setProducts(processedProducts)
      setError(null)
    } catch (error: any) {
      console.error('Error fetching removed products:', error)
      setError(
        error.response?.data?.message ||
          error.message ||
          formatMessage({id: 'REMOVED_PRODUCTS.FETCH_ERROR'})
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [auth?.token, language, appliedPoIdFilter])

  const handleApplyFilter = () => {
    setAppliedPoIdFilter(poIdFilterInput)
    // Reset to page 1 when filter is applied
    searchParams.set('current_page', '1')
    setSearchParams(searchParams)
  }

  const handleClearFilter = () => {
    setPoIdFilterInput('')
    setAppliedPoIdFilter(null)
    // Reset to page 1 when filter is cleared
    searchParams.set('current_page', '1')
    setSearchParams(searchParams)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApplyFilter()
    }
  }

  const handleImageClick = (imageUrl: string) => {
    setEnlargedImageUrl(imageUrl)
    setShowImageModal(true)
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setEnlargedImageUrl(null)
  }

  // Pagination calculations - products are already filtered by API
  const totalItems = products.length
  const totalPages = Math.ceil(totalItems / perPage)
  const startIndex = (currentPage - 1) * perPage
  const endIndex = startIndex + perPage
  const paginatedProducts = products.slice(startIndex, endIndex)

  // Reset to page 1 when filter changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      searchParams.set('current_page', '1')
      setSearchParams(searchParams)
    }
  }, [appliedPoIdFilter, totalPages])

  if (loading) {
    return <LoadingState />
  }

  if (error) {
    return <ErrorState message={error} />
  }

  return (
    <div className='removed-products-list'>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='card-title mb-0'>
            {formatMessage({id: 'REMOVED_PRODUCTS.PAGE_TITLE'})}
          </h3>
          <TutorialVideoLink
            section="removed_products"
            fallback="https://www.youtube.com/watch?v=0b2zj8Hve68"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-primary d-flex align-items-center ${
              showVideoHighlight ? 'video-highlight' : ''
            }`}
            onClick={() => {
              document.cookie = "removed_products_video=seen";
              setShowVideoHighlight(false);
            }}
          >
            <i className='fab fa-youtube text-danger fs-2'></i>
            <span className='ms-2'>
      {formatMessage({ id: 'video_link' })}
    </span>
          </TutorialVideoLink>
        </div>
        <div className='card-body'>
          {/* PO ID Filter */}
          <div className='mb-4'>
            <Form.Group>
              <Form.Label>{formatMessage({id: 'REMOVED_PRODUCTS.PO_ID'})}</Form.Label>
              <div className='d-flex align-items-start gap-2' style={{maxWidth: '600px'}}>
                <Form.Control
                  type='text'
                  placeholder={formatMessage({id: 'REMOVED_PRODUCTS.PO_ID_PLACEHOLDER'}) || 'Enter PO ID and press Enter or click Filter'}
                  value={poIdFilterInput}
                  onChange={(e) => setPoIdFilterInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{flex: '1'}}
                />
                <Button
                  variant='primary'
                  onClick={handleApplyFilter}
                  style={{borderRadius: '0.375rem'}}
                >
                  {formatMessage({id: 'FILTER'}) || 'Filter'}
                </Button>
                {appliedPoIdFilter && (
                  <Button
                    variant='outline-secondary'
                    onClick={handleClearFilter}
                    style={{borderRadius: '0.375rem'}}
                  >
                    {formatMessage({id: 'CLEAR'}) || 'Clear'}
                  </Button>
                )}
              </div>
              {appliedPoIdFilter && (
                <Form.Text className='text-muted'>
                  {formatMessage({id: 'REMOVED_PRODUCTS.FILTERED_RESULTS'}) || 'Showing'} {products.length} {formatMessage({id: 'REMOVED_PRODUCTS.RESULTS'}) || 'results'} {formatMessage({id: 'REMOVED_PRODUCTS.FOR_PO_ID'}) || 'for PO ID'} #{appliedPoIdFilter.replace(/^#/, '')}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {/* Table */}
          {paginatedProducts.length === 0 ? (
            <NoDataState />
          ) : (
            <>
              <RemovedProductsTable 
                products={paginatedProducts}
                onImageClick={handleImageClick}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='d-flex justify-content-center mt-5'>
                  <Paginator
                    totalResult={totalItems}
                    perPage={perPage}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        show={showImageModal}
        imageUrl={enlargedImageUrl || ''}
        onClose={handleCloseImageModal}
      />
    </div>
  )
}

export default RemovedProductsList

